import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  debounce,
  platformTransitionStyle,
} from '../../../../shared/helpers.js'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider/index'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import tts from '../../lib/TTS.js'

import type { SettingsScreenModalProps } from './types.js'

const SCROLL_DEBOUNCE_MS = 50
const SCROLL_INTERVAL = 100

const SettingsScreenModal = ({
  content,
  onClose,
  title,
}: SettingsScreenModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const { setKeepLogoVisible } = useSidePanel()

  const scrollContent = useCallback(
    (direction: 'UP' | 'DOWN', amount: number = SCROLL_INTERVAL) => {
      const container = contentRef.current
      if (!container) return

      const offset =
        direction === 'DOWN'
          ? container.scrollTop + amount
          : container.scrollTop - amount

      container.scrollTop = offset
    },
    []
  )

  const debouncedScroll = useCallback(
    debounce(
      (direction: 'UP' | 'DOWN', amount?: number) =>
        scrollContent(direction, amount),
      SCROLL_DEBOUNCE_MS
    ),
    [scrollContent]
  )

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      const container = contentRef.current

      if (!container) return

      const canScroll = container.scrollHeight > container.clientHeight

      if (!canScroll) return

      const deltaY = event.deltaY
      const scrollAmount = Math.abs(deltaY) * 2

      if (deltaY > 0) {
        scrollContent('DOWN', scrollAmount)
      } else if (deltaY < 0) {
        scrollContent('UP', scrollAmount)
      }
    },
    [scrollContent]
  )

  useKeyDown(
    [KeyTypeEnum.Back, KeyTypeEnum.Down, KeyTypeEnum.Up, KeyTypeEnum.Right],
    (key) => {
      switch (key) {
        case KeyTypeEnum.Back:
          return onClose()
        case KeyTypeEnum.Down:
          return debouncedScroll('DOWN')
        case KeyTypeEnum.Up:
          return debouncedScroll('UP')
        case KeyTypeEnum.Right:
          return
      }
    },
    { stopPropagation: true }
  )

  useEffect(() => {
    const textToRead = `${title}. ${content}`
    tts.addTextToRead(textToRead)

    setKeepLogoVisible(true)
    return () => {
      setKeepLogoVisible(false)
      tts.stopRead()
    }
  }, [])

  useEffect(() => {
    const container = contentRef.current

    if (container) {
      setHasOverflow(container.scrollHeight > container.clientHeight)
    }
    googleAnalytics4.sendPageViewEvent(title)
  }, [title, content])

  return createPortal(
    <div className="settings-scrolling-text-modal">
      <h1 className="settings-scrolling-text-title">{title}</h1>
      <div className="scroll-hide">
        <div
          ref={contentRef}
          className="content"
          tabIndex={-1}
          onWheel={handleWheel}
          style={{
            transition: platformTransitionStyle('transform 500ms ease-in-out'),
            overflowY: hasOverflow ? 'scroll' : 'hidden',
            scrollbarWidth: hasOverflow ? 'auto' : 'none',
            msOverflowStyle: hasOverflow ? 'scrollbar' : 'none',
            pointerEvents: 'auto',
          }}
        >
          {content}
        </div>
      </div>
    </div>,
    document.getElementById('full-width-background')!
  )
}

export default SettingsScreenModal
